import {
  useMarkAllAsRead,
  useNotifications,
  useUnreadImportNotificationsCount,
  useUnreadNotificationCount,
} from "creso-core/hooks";
import {
  Badge,
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  V2Loader,
} from "web-design-system";
import { BellIcon, Loader2, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation } from "@tanstack/react-router";
import { handleError } from "@/utils/error";
import { NoActionRequiredIcon } from "@core/components/icon/no-action";
import { NotificationType } from "creso-core/api";
import { NotificationComponent } from "./notification";
import { TABS } from "../config";

export function Notifications() {
  const location = useLocation();

  const { data: allUnreadNotificationsCount, refetch: refetchUnreadCount } =
    useUnreadNotificationCount();

  const {
    data: importantUnreadNotificationsCount,
    refetch: refetchImportantUnreadCount,
  } = useUnreadImportNotificationsCount();

  const [selectedTab, setSelectedTab] = useState(TABS[0].value);

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    refetch: refetchNotifications,
  } = useNotifications({ importantOnly: selectedTab === "important" });

  const { mutateAsync: markAllAsRead } = useMarkAllAsRead();

  const showMarkAllBtn = useMemo(() => {
    if (selectedTab === "important" && importantUnreadNotificationsCount) {
      return importantUnreadNotificationsCount?.unread_count > 0;
    }
    if (selectedTab === "others" && allUnreadNotificationsCount) {
      return (
        (allUnreadNotificationsCount?.unread_count ?? 0) -
          (importantUnreadNotificationsCount?.unread_count ?? 0) >
        0
      );
    }
    return false;
  }, [
    selectedTab,
    allUnreadNotificationsCount,
    importantUnreadNotificationsCount,
  ]);

  const notifications = useMemo(() => {
    return data?.pages.flatMap((page) =>
      page.results.filter(
        (notification) =>
          notification.event_type !== ("CHANGE_OF_BROKER" as NotificationType)
      )
    );
  }, [data]);

  const { inView, ref: loaderRef } = useInView();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [location.href]);

  const refetch = () => {
    refetchUnreadCount();
    refetchImportantUnreadCount();
    refetchNotifications();
  };

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const getNotificationCount = (tab: string) => {
    let count = null;
    if (tab === "all") count = allUnreadNotificationsCount?.unread_count;
    if (tab === "important")
      count = importantUnreadNotificationsCount?.unread_count;
    if (tab === "others")
      count =
        (allUnreadNotificationsCount?.unread_count ?? 0) -
        (importantUnreadNotificationsCount?.unread_count ?? 0);
    if (!count) return null;
    return count > 99 ? "99+" : `${count}`;
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          refetch();
        }
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <div className="relative">
          <Button size="icon" variant="ghost">
            <BellIcon />
          </Button>
          {allUnreadNotificationsCount?.unread_count ? (
            <div className="absolute -top-1.5 -right-1.5 text-center min-w-4 min-h-4 bg-destructive text-white text-xxs rounded-full p-1 py-0 flex items-center justify-center">
              {getNotificationCount("all")}
            </div>
          ) : null}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-125 p-0 overflow-hidden"
        side="right"
        align="end"
        sideOffset={-40}
        alignOffset={-120}
        portal
      >
        <ScrollArea className="h-[calc(100vh-25px)] flex flex-col">
          <div className="flex flex-col flex-1">
            <div className="pt-4 sticky top-0 pb-2 flex bg-background items-center-safe z-2 pl-6 pr-4">
              <span className="text-lg font-semibold mr-auto">
                Notifications{" "}
                {allUnreadNotificationsCount?.unread_count
                  ? `(${getNotificationCount("all")})`
                  : ""}
              </span>
              <Button
                onClick={() => setIsOpen(false)}
                size="icon"
                variant="ghost"
              >
                <XIcon />
              </Button>
            </div>
            <Tabs
              className="flex-1 bg-background"
              value={selectedTab}
              onValueChange={(v) => setSelectedTab(v)}
            >
              <div className="w-full px-5 items-center bg-background flex sticky pb-2 z-10 top-15">
                <TabsList className="sticky top-15 z-10">
                  {TABS.map((t) => (
                    <TabsTrigger key={t.value} value={t.value}>
                      {t.label}
                      {getNotificationCount(t.value) ? (
                        <Badge
                          variant="custom"
                          className={cn(
                            "px-1 flex justify-center h-5 min-w-5 text-center py-1 ml-2",
                            t.value === "important" &&
                              "bg-tag-orange text-tag-orange-foreground",
                            t.value === "others" &&
                              "bg-tag-grey text-tag-grey-foreground",
                            t.value === "others" &&
                              selectedTab !== "others" &&
                              "bg-background"
                          )}
                        >
                          {getNotificationCount(t.value)}
                        </Badge>
                      ) : null}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <Button
                  onClick={() =>
                    markAllAsRead(selectedTab === "important").catch((e) =>
                      handleError(e, "Something went wrong")
                    )
                  }
                  className={cn(
                    "text-foreground ml-auto text-sm font-normal hidden",
                    showMarkAllBtn && "inline-flex"
                  )}
                  variant="ghost"
                  size="sm"
                >
                  Mark all as read
                </Button>
              </div>
              {TABS.map((t) => (
                <TabsContent className="flex-1" key={t.value} value={t.value}>
                  <V2Loader isLoading={isLoading}>
                    {notifications?.length ? (
                      <>
                        <div className="flex flex-col">
                          {notifications?.map((notification) => (
                            <NotificationComponent
                              key={notification?.id}
                              notification={notification}
                              onNavigate={() => setIsOpen(false)}
                            />
                          ))}
                        </div>
                        {hasNextPage && (
                          <div
                            ref={loaderRef}
                            className="h-10 w-full p-3 flex justify-center items-center"
                          >
                            <Loader2 className="animate-spin" />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="px-4 pb-4 h-full">
                        <div className="flex flex-col items-center justify-center h-full border border-dashed border-border rounded-2xl p-4">
                          <NoActionRequiredIcon />
                          <span className="text-sm text-muted-foreground">
                            You are all caught up
                          </span>
                        </div>
                      </div>
                    )}
                  </V2Loader>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </ScrollArea>
      </PopoverContent>
      {isOpen && (
        <div
          className={cn(
            "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
      )}
    </Popover>
  );
}
